import { useTranslation } from 'react-i18next';
import type { PricingStrategyProfileResponse } from '@/shared/api';
import { formatDate, formatNumber } from '@/shared/lib/format';
import { ConfigurationDetails } from './ConfigurationDetails';
import { formatProfilePercent } from './profileValueFormat';

export function ProfileCalibrationDetails({
  profile,
}: {
  profile: PricingStrategyProfileResponse;
}) {
  const { t } = useTranslation();
  const calibration = profile.calibration;
  const counts = [
    ['activeApartmentCount', calibration.activeApartmentCount],
    ['bookingCount', calibration.bookingCount],
    ['confirmedBookingCount', calibration.confirmedBookingCount],
    ['cancelledBookingCount', calibration.cancelledBookingCount],
    ['confirmedNightCount', calibration.confirmedNightCount],
    ['regularDemandEventCount', calibration.regularDemandEventCount],
    ['regularDemandStillConfirmedCount', calibration.regularDemandStillConfirmedCount],
    ['regularDemandLaterCancelledCount', calibration.regularDemandLaterCancelledCount],
    ['excludedImportedBookingCount', calibration.excludedImportedBookingCount],
    ['sameDayBookingCount', calibration.sameDayBookingCount],
    ['sameDayBookingFrom15LocalCount', calibration.sameDayBookingFrom15LocalCount],
    ['operatingDayCount', calibration.operatingDayCount],
    ['historicalBaseRateBookingCount', calibration.historicalBaseRateBookingCount],
    ['historicalCleaningFeeBookingCount', calibration.historicalCleaningFeeBookingCount],
  ] as const;
  return (
    <ConfigurationDetails title="Kalibrierungsdaten anzeigen">
      <div className="grid gap-5">
        <dl className="@xl:grid-cols-2 grid gap-2 text-sm">
          <Fact
            label={t('admin.dynamicPricing.profiles.dataAsOfLabel')}
            value={formatDate(calibration.snapshotDate)}
          />
          <Fact
            label={t('admin.dynamicPricing.profiles.operationalFrom')}
            value={formatDate(calibration.operationalDataFrom)}
          />
          <Fact
            label={t('admin.dynamicPricing.profiles.source')}
            value={t(`admin.dynamicPricing.profiles.values.${calibration.sourceKind}`)}
          />
          <Fact
            label={t('admin.dynamicPricing.profiles.dataLimit')}
            value={t(`admin.dynamicPricing.profiles.values.${calibration.dataLimit}`)}
          />
          {counts.map(([label, value]) => (
            <Fact
              key={label}
              // Gezählt wird der anonymisierte Datensatz, nicht der eigene Bestand.
              label={
                label === 'activeApartmentCount'
                  ? 'Apartments im Datensatz'
                  : t(`admin.dynamicPricing.profiles.calibration.${label}`)
              }
              value={formatNumber(value)}
            />
          ))}
        </dl>
        <ShareRows profile={profile} />
        <p className="text-ink-soft text-sm">
          {t('admin.dynamicPricing.profiles.unsupportedSignals', {
            seasonality: yesNo(calibration.supportsSeasonality, t),
            probability: yesNo(calibration.supportsNonBookingProbability, t),
            elasticity: yesNo(calibration.supportsPriceElasticity, t),
            selfLearning: yesNo(calibration.supportsSelfLearningReferencePrice, t),
          })}
        </p>
      </div>
    </ConfigurationDetails>
  );
}

function ShareRows({ profile }: { profile: PricingStrategyProfileResponse }) {
  const { t } = useTranslation();
  const calibration = profile.calibration;
  return (
    <div className="grid gap-2 text-sm">
      {calibration.leadTimeShares.map((share) => (
        <Fact
          key={share.id}
          label={t('admin.dynamicPricing.profiles.leadShare', { days: share.maximumLeadDays })}
          value={formatProfilePercent(share.cumulativeSharePercent)}
        />
      ))}
      {calibration.stayLengthShares.map((share) => (
        <Fact
          key={share.id}
          label={t(`admin.dynamicPricing.profiles.values.${share.band}`)}
          value={formatProfilePercent(share.sharePercent)}
        />
      ))}
      {calibration.farOutDemandShares.map((share) => (
        <Fact
          key={share.id}
          label={t('admin.dynamicPricing.profiles.farOutShare', {
            days: share.moreThanLeadDays,
            count: share.eventCount,
          })}
          value={formatProfilePercent(share.sharePercent)}
        />
      ))}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
      <dt className="text-ink-soft">{label}</dt>
      <dd className="text-ink text-right font-medium tabular-nums">{value}</dd>
    </div>
  );
}

const yesNo = (value: boolean, t: ReturnType<typeof useTranslation>['t']) =>
  t(`admin.dynamicPricing.profiles.values.${value ? 'yes' : 'no'}`);
